/* Shared building blocks for the viewer "inner" pages (category detail,
   search, watchlist). Styled to match /profile: Montserrat, black surface, the
   floating capsule nav pill, and the exact profile card
   (VideoAutoPlayer + TitleLabel in ImageGroup's hoverZoom). */
import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { Box, IconButton, Tooltip } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import LockIcon from '@mui/icons-material/Lock';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Pathname } from 'Routes';
import { AppContext } from 'Context';
import VideoAutoPlayer from 'Components/ImageGroup/video-autoplayer';
import { TitleLabel } from 'Components/ImageGroup/video-autoplayer/TitleLabel';
import imgStyles from 'Components/ImageGroup/index.module.css';
import Aurora from 'Components/reactbits/Aurora';
import SoftAuroraGL from 'Components/reactbits/SoftAuroraGL';
import { fxKind, bgSurface, DEEP_AURORA_BG } from 'hooks/useProfileBg';
import AlterImage from '../../Assets/alter-image.png';

/* ── Default projector backdrop ────────────────────────────────────────────
   A deep mixed gradient: three very dark colour fields (navy / violet / steel
   blue) bleeding into a near-black base, with the animated aurora dimmed on
   top for movement. Every value here is well below mid-grey, so white titles
   and card art keep full contrast — the previous default washed out because
   the shader ran at full strength over a #292e40 base.

   Kept as shared constants so /profile, search, watchlist, innerpage and the
   Layout preview all paint the identical surface. */

// Bottom layer — the "mix" itself, pure CSS so it paints instantly on load.
// Defined in hooks/useProfileBg (bgSurface paints it for the Deep Aurora theme)
// and re-exported here under its long-standing name.
export const DEFAULT_PROJECTOR_BG = DEEP_AURORA_BG;

// Flat stand-in for preview chips / surfaces that can only take one colour.
export const DEFAULT_PROJECTOR_BG_SOLID = '#06070c';

// Animated layer, deliberately dim: dark stops, low amplitude, and a 0.42
// opacity wrapper so it reads as a slow glow inside the gradient rather than
// as a light band across the page.
export const DEFAULT_AURORA_OPACITY = 0.42;
export const DEFAULT_AURORA_STOPS = ['#1b2c4d', '#3a2a63', '#122038'];

// Top scrim — flattens whatever the shader does behind the content column and
// keeps the top/bottom edges near-black so text never loses contrast.
export const DEFAULT_AURORA_SCRIM = 'linear-gradient(180deg, rgba(3, 4, 7, 0.55) 0%, rgba(3, 4, 7, 0.12) 26%, rgba(3, 4, 7, 0.18) 62%, rgba(3, 4, 7, 0.62) 100%)';

// The animated part of the default backdrop (dimmed aurora + scrim). Split out
// so /profile can drop it into its own fixed .auroraLayer.
export const ProjectorAuroraFX = () => (
  <>
    <div style={{ position: 'absolute', inset: 0, opacity: DEFAULT_AURORA_OPACITY }}>
      <Aurora
        colorStops={DEFAULT_AURORA_STOPS}
        amplitude={0.55}
        blend={0.65}
        speed={0.7}
      />
    </div>
    <div style={{ position: 'absolute', inset: 0, background: DEFAULT_AURORA_SCRIM }} />
  </>
);

// The /access page's Soft Aurora backdrop, offered as a projector theme
// (PROFILE_BG_SOFT_AURORA). Same shader and same parameters as
// Pages/private/access — keep the two in sync so picking the theme really does
// reproduce that page.
export const SoftAuroraFX = () => (
  <SoftAuroraGL
    speed={0.5}
    scale={1.05}
    brightness={1.35}
    color1="#5aa5ef"
    color2="#6d5efc"
    bandHeight={0.5}
    bandSpread={0.5}
    layerOffset={2.5}
  />
);

// The exact backdrop used on /profile, so search / watchlist / innerpage share
// the same look (and nothing black shows behind the nav pill).
// `color` mirrors the projector_background preference: a solid preset paints a
// flat colour; empty ('' = Default) and the Soft Aurora sentinel run that
// shader over black; the Deep Aurora sentinel paints the original mixed
// gradient with ProjectorAuroraFX on top.
export const ViewerAurora = ({ color = '' }) => (
  <div
    aria-hidden="true"
    style={{
      position: 'fixed',
      inset: 0,
      zIndex: 0,
      pointerEvents: 'none',
      background: bgSurface(color) || DEFAULT_PROJECTOR_BG,
    }}
  >
    {fxKind(color) === 'soft' && <SoftAuroraFX />}
    {fxKind(color) === 'deep' && <ProjectorAuroraFX />}
  </div>
);

// Dark viewer theme (Montserrat, black surface) matching /profile + /access.
const viewerTheme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: '#000', paper: '#120F17' },
    primary: { main: '#5aa5ef' },
    text: { primary: '#ffffff', secondary: 'rgba(255,255,255,0.6)' },
  },
  typography: { fontFamily: '"Montserrat", sans-serif' },
  shape: { borderRadius: 12 },
});

export const ViewerThemeProvider = ({ children }) => (
  <ThemeProvider theme={viewerTheme}>{children}</ThemeProvider>
);

// locked_for is a comma-separated list of plan IDs the content is locked to.
export const isLockedForPlan = (lockedFor, currentPlanId) => {
  if (!lockedFor || !currentPlanId) return false;
  return String(lockedFor).split(',').map((s) => s.trim()).includes(String(currentPlanId));
};

// The grid track — landscape cards ~ profile carousel width.
const GRID_SX = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
  gap: { xs: 0.5, md: 1 },
};

// ── Grid-shaped shimmer (matches the card grid). ───────────────────────────
export const GridShimmer = ({ count = 15 }) => (
  <Box sx={GRID_SX}>
    {Array.from({ length: count }).map((_, i) => (
      <Box key={i} sx={{ px: { xs: '10px', md: '15px' } }}>
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '8px',
            height: 200,
            bgcolor: 'rgba(255,255,255,0.07)',
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              transform: 'translateX(-100%)',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.09), transparent)',
              animation: 'viewerShimmer 0.9s infinite',
            },
            '@keyframes viewerShimmer': { '100%': { transform: 'translateX(100%)' } },
          }}
        />
        <Box sx={{ height: 12, borderRadius: 1, mt: 1, width: '70%', bgcolor: 'rgba(255,255,255,0.07)' }} />
      </Box>
    ))}
  </Box>
);

// ── One card — the exact /profile card (VideoAutoPlayer + TitleLabel). ─────
const ViewerCard = ({
  video, isPreview, locked, onLockedClick, onRemove,
}) => {
  const {
    id, thumbnails, draft_thumbnails, title, SubCategory,
  } = video;
  const to = isPreview ? Pathname.getPlayerPath(id, 'play') : Pathname.getVideoPath(id);
  const thumb = thumbnails || draft_thumbnails || AlterImage;
  return (
    <Box className={cn(imgStyles.hoverZoom, { [imgStyles.videoBlurred]: locked })} sx={{ position: 'relative' }}>
      <Link
        to={to}
        className={imgStyles.imageGroupImageLink}
        onClick={(e) => { if (locked) { e.preventDefault(); if (onLockedClick) onLockedClick(); } }}
      >
        <VideoAutoPlayer
          videoDetails={video}
          thumbNail={thumb}
          videoID={id}
          isLocked={locked}
          onLockedClick={onLockedClick}
        />
        <TitleLabel title={title} category={SubCategory} />
      </Link>
      {locked && (
        <div className={imgStyles.lockIconOverlay}>
          <LockIcon />
        </div>
      )}
      {onRemove && (
        <Tooltip title="Remove from watchlist">
          <IconButton
            size="small"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(video); }}
            sx={{
              position: 'absolute', top: 6, right: 16, zIndex: 2, width: 32, height: 32, minWidth: 32, flexShrink: 0, p: 0, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', backdropFilter: 'blur(4px)', '&:hover': { bgcolor: '#ef4444' },
            }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export const ViewerGrid = ({
  videos = [], isPreview = false, onLockedClick, onRemove,
}) => {
  const { subscriptionDetails } = useContext(AppContext);
  const currentPlanId = subscriptionDetails?.subscription?.plan_id
    || subscriptionDetails?.plans?.[subscriptionDetails?.plans?.length - 1]?.plan_id;
  return (
    <Box sx={GRID_SX}>
      {videos.map((v) => (
        <ViewerCard
          key={v.id}
          video={v}
          isPreview={isPreview}
          locked={isLockedForPlan(v.locked_for, currentPlanId)}
          onLockedClick={onLockedClick}
          onRemove={onRemove}
        />
      ))}
    </Box>
  );
};
